import Typography from '@mui/material/Typography';
import React from 'react';
import { ChevronLeft } from 'shared/assets/icons';
import { Collapse } from 'shared/components/ui/collapse';
import {
  Colors,
  IconButton,
  Sizes as IconButtonSizes,
} from 'shared/components/ui/icon-button';
import { useToggle } from 'shared/hooks/use-toggle';

import * as Styled from './section.style';
import { Sizes } from './types';

export type SectionProps = {
  children: React.ReactNode;
  title: string;
  titleCount?: number;
  titleVariant?: React.ComponentProps<typeof Typography>['variant'];
  canCollapse?: boolean;
  initialOpen?: boolean;
  actions?: React.ReactNode[];
  size?: Sizes;
};

export const Section: React.FC<SectionProps> = ({
  title,
  titleCount,
  titleVariant,
  actions = [],
  initialOpen,
  canCollapse,
  size = Sizes.Medium,
  children,
}) => {
  const [open, toggleOpen] = useToggle(initialOpen);
  const CollapseContainer = canCollapse ? Collapse : React.Fragment;
  const collapseContainerProps = canCollapse ? { isOpen: open } : {};
  const titleTypography =
    titleVariant ?? (size === Sizes.Small ? 'buttonSmall' : 'buttonSmall');

  return (
    <>
      <Styled.Container
        $size={size}
        $canCollapse={!!canCollapse}
        onClick={canCollapse ? toggleOpen : undefined}
      >
        <Styled.TitleContainer>
          <Typography variant={titleTypography} component="span">
            {title}
          </Typography>
          {titleCount !== undefined && (
            <Styled.TitleCount>{titleCount}</Styled.TitleCount>
          )}
        </Styled.TitleContainer>

        {!!actions.length && (
          <Styled.ActionsContainer>
            {actions.map((action, index) => (
              <React.Fragment key={`action-${index}`}>{action}</React.Fragment>
            ))}
          </Styled.ActionsContainer>
        )}
        {canCollapse && (
          <Styled.CollapseButton>
            <Styled.IconButtonContainer>
              <IconButton
                icon={ChevronLeft}
                rotation={open ? -90 : 0}
                size={IconButtonSizes.Small}
                color={Colors.Default}
                as="span"
              />
            </Styled.IconButtonContainer>
          </Styled.CollapseButton>
        )}
      </Styled.Container>

      <CollapseContainer {...collapseContainerProps}>
        {children}
      </CollapseContainer>
    </>
  );
};
